import moment from "moment";
import { safeMoment } from "@/utils/safeMoment";

import React from "react";

interface Realtor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  join_date: string;
}

interface Owner {
  id: string;
  full_name: string;
  email: string;
  phone_number: string;
}

interface PropertyListing {
  id: string;
  proposedLocation: string;
  status: string;
  investmentType: string;
  description: string;
  totalInvestmentAmount: number;
  amountAlreadyRaised: number;
  percentageRaised: number;
  minInvestmentAmountPerSubscribers: number;
  expectedROI: number;
  developmentStage: string;
  bathroomCount: number;
  bedroomCount: number;
  furnished: boolean;
  propertyType: string;
  realtor: Realtor;
  createdDate: string;
  owner: Owner;
  image_url: string[];
}

const PropertyTable: React.FC<{ listing: PropertyListing }> = ({ listing }) => {
  return (
    <div
      className={`cursor-pointer flex items-center gap-4 p-4 border-b border-soft-200 bg-white hover:bg-grey-6 transition-colors ease-linear delay-150`}
    >
      <div className="min-w-[100px] text-sm ml-2 text-grey-7">
        {safeMoment(listing.createdDate)?.format("DD MMMM, YYYY") ?? "-"}
      </div>

      <div className="ml-[40px] w-16 h-12 overflow-hidden rounded-md">
        <img
          // image_url[1]
          src={
            listing?.image_url?.length > 0
              ? listing?.image_url[0]
              : "/assets/images/property-default.svg"
          }
          alt={"property"}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="flex-grow">
        <h3 className="text-main-900 text-base min-w-[274px] max-w-[274px] truncate font-bold">
          {listing.description}
        </h3>
        <div className="flex items-center gap-2 text-grey-7 text-sm mt-1">
          <div className="flex items-center gap-1">
            <svg
              width="9"
              height="12"
              viewBox="0 0 9 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.0117188 11.2516C0.0117188 10.1377 0.454218 9.06937 1.24187 8.28171C2.02952 7.49406 3.09781 7.05156 4.21172 7.05156C5.32563 7.05156 6.39391 7.49406 7.18157 8.28171C7.96922 9.06937 8.41172 10.1377 8.41172 11.2516H7.36172C7.36172 10.4161 7.02984 9.61491 6.43911 9.02418C5.84837 8.43344 5.04715 8.10156 4.21172 8.10156C3.37629 8.10156 2.57507 8.43344 1.98433 9.02418C1.39359 9.61491 1.06172 10.4161 1.06172 11.2516H0.0117188ZM4.21172 6.52656C2.47134 6.52656 1.06172 5.11694 1.06172 3.37656C1.06172 1.63619 2.47134 0.226562 4.21172 0.226562C5.95209 0.226562 7.36172 1.63619 7.36172 3.37656C7.36172 5.11694 5.95209 6.52656 4.21172 6.52656ZM4.21172 5.47656C5.37197 5.47656 6.31172 4.53681 6.31172 3.37656C6.31172 2.21631 5.37197 1.27656 4.21172 1.27656C3.05147 1.27656 2.11172 2.21631 2.11172 3.37656C2.11172 4.53681 3.05147 5.47656 4.21172 5.47656Z"
                fill="#2D96A7"
              />
            </svg>

            <span className=" max-w-[132px] truncate">
              {`${listing.owner.full_name}`}
            </span>
          </div>
          <span>•</span>
          <div className="flex items-center gap-1">
            <svg
              width="15"
              height="14"
              viewBox="0 0 15 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.21133 11.6725L9.81008 9.07371C10.324 8.55974 10.674 7.90491 10.8157 7.19204C10.9575 6.47917 10.8847 5.74027 10.6066 5.06878C10.3284 4.39728 9.85735 3.82334 9.25301 3.41955C8.64867 3.01575 7.93816 2.80022 7.21133 2.80022C6.4845 2.80022 5.77399 3.01575 5.16965 3.41955C4.56531 3.82334 4.09427 4.39728 3.81611 5.06878C3.53795 5.74027 3.46515 6.47917 3.60692 7.19204C3.74869 7.90491 4.09866 8.55974 4.61258 9.07371L7.21133 11.6725ZM7.21133 13.1572L3.87023 9.81606C3.20943 9.15525 2.75943 8.31334 2.57712 7.39678C2.39481 6.48022 2.48838 5.53018 2.846 4.66681C3.20363 3.80343 3.80925 3.06549 4.58627 2.5463C5.36329 2.02711 6.27682 1.75 7.21133 1.75C8.14584 1.75 9.05937 2.02711 9.83639 2.5463C10.6134 3.06549 11.219 3.80343 11.5767 4.66681C11.9343 5.53018 12.0279 6.48022 11.8455 7.39678C11.6632 8.31334 11.2132 9.15525 10.5524 9.81606L7.21133 13.1572ZM7.21133 7.52496C7.48981 7.52496 7.75688 7.41433 7.95379 7.21742C8.1507 7.02051 8.26133 6.75344 8.26133 6.47496C8.26133 6.19648 8.1507 5.92941 7.95379 5.7325C7.75688 5.53558 7.48981 5.42496 7.21133 5.42496C6.93285 5.42496 6.66578 5.53558 6.46887 5.7325C6.27195 5.92941 6.16133 6.19648 6.16133 6.47496C6.16133 6.75344 6.27195 7.02051 6.46887 7.21742C6.66578 7.41433 6.93285 7.52496 7.21133 7.52496ZM7.21133 8.57496C6.65438 8.57496 6.12023 8.35371 5.72641 7.95988C5.33258 7.56606 5.11133 7.03191 5.11133 6.47496C5.11133 5.918 5.33258 5.38386 5.72641 4.99003C6.12023 4.59621 6.65438 4.37496 7.21133 4.37496C7.76828 4.37496 8.30243 4.59621 8.69625 4.99003C9.09008 5.38386 9.31133 5.918 9.31133 6.47496C9.31133 7.03191 9.09008 7.56606 8.69625 7.95988C8.30243 8.35371 7.76828 8.57496 7.21133 8.57496Z"
                fill="#2D96A7"
              />
            </svg>

            <span className="text-sm text-grey-7 min-w-[191px] max-w-[191px] truncate">
              {listing.proposedLocation}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <span
          className={`flex gap-1 items-center px-3 py-1 rounded-lg text-sm ${
            listing.status === "approved"
              ? "bg-green-success text-main-900"
              : listing.status === "pending"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-red-100 text-red-800"
          }`}
        >
          <svg
            width="5"
            height="4"
            viewBox="0 0 5 4"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="2.21094" cy="2" r="2" fill="#2D96A7" />
          </svg>

          {listing.status}
        </span>
        {/* 
        <div className="flex flex-col">
          <span className="text-sm text-grey-7">
            Investment: ${listing.totalInvestmentAmount.toLocaleString()}
          </span>
          <span className="text-sm text-grey-7">
            Raised: {listing.percentageRaised}%
          </span>
        </div> */}

        <button className="flex items-center p-2 gap-1 w-[130px] h-[40px] text-xs border border-soft-200 text-main-900 hover:text-bnp-cyan rounded-lg">
          <svg
            width="17"
            height="16"
            viewBox="0 0 17 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.2109 4.66536V11.332C14.2109 13.332 13.2109 14.6654 10.8776 14.6654H5.54427C3.21094 14.6654 2.21094 13.332 2.21094 11.332V4.66536C2.21094 2.66536 3.21094 1.33203 5.54427 1.33203H10.8776C13.2109 1.33203 14.2109 2.66536 14.2109 4.66536Z"
              stroke="#9EA3AE"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8.21072 9.66753C8.82437 9.66753 9.32183 9.17007 9.32183 8.55642C9.32183 7.94277 8.82437 7.44531 8.21072 7.44531C7.59707 7.44531 7.09961 7.94277 7.09961 8.55642C7.09961 9.17007 7.59707 9.66753 8.21072 9.66753Z"
              stroke="#9EA3AE"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.1205 12.5556C10.1205 11.6956 9.26496 11 8.21162 11C7.15829 11 6.30273 11.6956 6.30273 12.5556"
              stroke="#9EA3AE"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9.87695 3V4.33333C9.87695 5.06667 10.477 5.66667 11.2103 5.66667H12.5436"
              stroke="#9EA3AE"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          View detail
        </button>
      </div>
    </div>
  );
};

export default PropertyTable;
